import * as S from './Preparation.styles'
import React, { useEffect, useState } from 'react'
import CourseInfo from '@app/components/molecules/CourseInfo'
import { CourseSettings } from '@app/evaluation-settings-menu/CourseSettings'
import EditCourseSettingsModal from './EditCourseSettingsModal'

import { PreparationTeam } from './PreparationTeam'
import Spinner from './Spinner'
import { useRoute } from '@react-navigation/native'
import { State } from '@app/state/types'
import { useEvaluation } from '@common/hooks'
import { useSelector } from 'react-redux'
import { useSnackbar } from 'react-simple-snackbar'
import useUser from '@common/hooks/useUser'
import {
  clearEvaluationState,
  clearMixedEvaluationData,
  deleteEvaluation,
} from '@app/evaluation-core/evaluation'
import { navigate } from '@app/navigation'
import loadingErrorHook from '@common/hooks/loadingErrorHook'
import { LRClub, LRCourse } from '../../types/Clubs'
import useEvaluationUser from '@common/hooks/useEvaluationUser'
import { useDispatch } from 'react-redux'
import RoundedButton from '@app/ui/rounded-button/RoundedButton'
import HeaderBar from '@app/courses/components/HeaderBar'
import { useMediaQuery } from 'react-responsive'
import theme from '@app/ui/theme'
import { fetch } from '../../../common/fetch'
import getEnv from '@app/environments/getEnv'
import { useAxios } from '@common/hooks/useAxios'
import { EvalStatus } from '@app/courses/domain/ratingStatus'

let shouldClearTimeout: NodeJS.Timeout | null = null

// The trigger that fills in the holes is allowed a minute, and a cold start
// comes on top of that. Giving up earlier threw away ratings that were still
// on their way in. A failed initialization reports itself through
// evaluation.loading, so this only covers the case where nothing answers.
const INITIALIZATION_TIMEOUT_MS = 90000

const INITIALIZATION_TIMEOUT_TEXT =
  'Preparing the rating is taking longer than expected. It may still finish, so check In Progress Ratings in a moment. If it does not show up, please contact Mapping Industries.'

type Props = { club: LRClub }

export const Preparation: React.FC<Props> = ({ club }) => {
  const { currentId, evaluation } = useEvaluation()
  const evaluationUser = useEvaluationUser()
  const { id: userId, isAdmin } = useUser()
  const [openSnackbar] = useSnackbar()
  const dispatch = useDispatch()
  const isSmallScreen = useMediaQuery({ query: '(max-width: 960px)' })
  const [fileName, setFileName] = useState('')
  const [showSpinner, setShowSpinner] = useState(false)
  const courses: LRCourse[] = useSelector(
    (state: State) => state.firestore.data.courses
  )
  const clubs: LRClub[] = useSelector(
    (state: State) => state.firestore.data.clubs
  )
  const originalClubName = clubs
    ? (Object.values(clubs) as LRClub[]).find(
        (c: any) => c.id?.toString() === evaluation?.clubId?.toString()
      )?.name
    : undefined
  const copyAxios = useAxios('/evaluations/copy')
  const route = useRoute<any>()

  const [isCopying, setIsCopying] = useState(false)
  const [showCourseSettingsModal, setShowCourseSettingsModal] = useState(false)

  const handleSave = async () => {
    setShowSpinner(true)
    if (fileName !== '') {
      openSnackbar('Rating saved')
      try {
        await fetch.post(`${getEnv().api}/evaluations/save`, {
          evaluationId: currentId,
          fileName,
        })
        setShowSpinner(false)
      } catch (e) {
        setShowSpinner(false)
        openSnackbar(
          'There was an error saving the evaluation. Please try again.'
        )
      }
      return
    }
    openSnackbar('Please enter an evaluation name')
    setShowSpinner(false)
  }

  const fetchingData =
    !evaluation || !evaluation?.holes || !userId || !currentId

  // Leaves the rating where it is. It may still be initializing, and removing
  // it from the database here left the firestore document behind as a row that
  // only ever spins. Removing a rating is what the Delete action is for.
  const leaveInitialization = () => {
    setTimeout(clearEvaluationState, 1000)
    return navigate('Courses', null)
  }

  // A rating the backend could not initialize holds nothing to come back to,
  // so take it out everywhere rather than leaving half of it behind.
  const discardFailedEvaluation = async () => {
    if (evaluation.isMixed) {
      return clearMixedEvaluationData()
    }
    if (currentId) {
      await deleteEvaluation(currentId)
    }
    setTimeout(clearEvaluationState, 1000)
    return navigate('Courses', null)
  }

  loadingErrorHook(
    fetchingData,
    leaveInitialization,
    INITIALIZATION_TIMEOUT_MS,
    INITIALIZATION_TIMEOUT_TEXT,
    () => ({
      hasEvaluation: !!evaluation,
      hasHoles: !!evaluation?.holes,
      userId,
      currentId,
      evaluationLoading: evaluation?.loading,
      evaluationStatus: evaluation?.status,
      clubId: evaluation?.clubId,
      courseId: evaluation?.courseId,
    })
  )

  useEffect(() => {
    if (!evaluation?.loading) return

    const { loading, message } = evaluation.loading

    if (loading === 3) {
      openSnackbar(message)
      discardFailedEvaluation()
    }
  }, [evaluation?.loading])

  useEffect(() => {
    if (!evaluation?.loading) return
    const { loading } = evaluation.loading
    if (loading !== 1 && shouldClearTimeout) {
      clearTimeout(shouldClearTimeout)
      shouldClearTimeout = null
    }
  }, [evaluation.loading, shouldClearTimeout])

  useEffect(() => {
    if (evaluation?.loading.loading === 1 || isCopying) {
      return
    }
    const originalIdParam = route.params?.copyOriginalId
    if (!originalIdParam) {
      return
    }
    setIsCopying(true)

    copyAxios
      .post('', {
        originalId: originalIdParam,
        newId: currentId,
        userId: evaluationUser.userId,
      })
      .then((resp) => {
        console.log('copy response', resp)
        if (resp.status !== 200) {
          openSnackbar('Error copying rating')
        } else {
          openSnackbar('Rating copied successfully')
        }
        setIsCopying(false)
        route.params.copyOriginalId = null
      })
      .catch(() => {
        openSnackbar('Error copying rating')
        setIsCopying(false)
        route.params.copyOriginalId = null
      })
  }, [evaluation.loading, route.params, currentId])

  function getCourseFromEvaluation() {
    if (!evaluation?.courseId) return null

    const courseId = evaluation.courseId.toString()

    const course = Object.values(courses).find(
      (course: any) => course.id === courseId
    )
    return course
  }

  if (fetchingData) {
    return <Spinner title="Loading data from LiveCR..." />
  }

  if (isCopying) {
    return <Spinner title="Copying rating..." />
  }

  const canEdit =
    evaluation?.status !== EvalStatus.FINALIZED && evaluationUser.isAdmin

  return (
    <S.ContentMain>
      <PreparationTeam />
      <S.ContentCard>
        <CourseInfo evaluation={evaluation} originalClubName={originalClubName} />
      </S.ContentCard>
      <S.ContentCard>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <S.ContentHeading>Course settings</S.ContentHeading>
          <div
            onClick={() => {
              if (!canEdit) {
                openSnackbar(
                  'This rating is finalized. To change the course settings, create a new revision instead.'
                )
              }
            }}
          >
            <RoundedButton
              title="Edit Course Settings"
              disabled={!canEdit}
              onPress={() => setShowCourseSettingsModal(true)}
            />
          </div>
        </div>
        <S.ContentSeparator />
        <CourseSettings withValidationLabels interactive={false} />
        <S.ContentSeparator />
      </S.ContentCard>
      {canEdit && (
        <S.ContentCard>
          <HeaderBar
            isSmallScreen={isSmallScreen}
            debounceAmount={0}
            placeholder={isSmallScreen ? 'Name' : 'Rating Name'}
            onChange={({ target }) => setFileName(target.value)}
            value={fileName}
            icon="save"
            iconSize={30}
            color={theme.colors.primary}
            iconOnPress={handleSave}
            loading={showSpinner}
          />
        </S.ContentCard>
      )}
      <EditCourseSettingsModal
        isOpen={showCourseSettingsModal}
        onClose={() => setShowCourseSettingsModal(false)}
      />
    </S.ContentMain>
  )
}
